// Cliente dos comandos do quadro. A autorização real fica no servidor (kanban:gerenciar).
import { useCallback } from 'react';
import type { KanbanCartao, KanbanComandoResultado, KanbanConfirmacaoLiberacao, NivelPrioridadeKanban } from '@pieltec/contracts';
import { apiMutate } from '../../../lib/csrf-fetch';

export const MENSAGEM_FALHA_MOVER = 'Não foi possível mover o cartão. Tente novamente.';
export const MENSAGEM_FALHA_LIBERAR = 'Não foi possível liberar OP(s). Tente novamente.';

export type ResultadoComando =
  | { readonly tipo: 'ok'; readonly resultado: KanbanComandoResultado | null }
  | { readonly tipo: 'conflito'; readonly codigo: string; readonly message: string }
  | { readonly tipo: 'erro'; readonly codigo?: string; readonly message: string };

async function interpretar(resposta: Response, mensagemPadrao: string = MENSAGEM_FALHA_MOVER): Promise<ResultadoComando> {
  if (resposta.ok) {
    const resultado = (await resposta.json()) as KanbanComandoResultado;
    return { tipo: 'ok', resultado };
  }

  let codigo: string | undefined;
  let message = mensagemPadrao;
  try {
    const corpo = (await resposta.json()) as { codigo?: unknown; message?: unknown };
    if (typeof corpo.codigo === 'string') codigo = corpo.codigo;
    if (typeof corpo.message === 'string' && corpo.message.trim().length > 0) {
      message = corpo.message;
    }
  } catch {
    // Corpo vazio ou não-JSON: a UI mostra a mensagem genérica como texto.
  }

  if (resposta.status === 409 && codigo === 'CONFLITO_CONCORRENTE') {
    return { tipo: 'conflito', codigo, message };
  }
  return { tipo: 'erro', codigo, message };
}

async function enviar(path: string, method: string, body: unknown): Promise<ResultadoComando> {
  try {
    const resposta = await apiMutate(path, { method, body });
    return await interpretar(resposta);
  } catch {
    return { tipo: 'erro', message: MENSAGEM_FALHA_MOVER };
  }
}

export function useKanbanComandos() {
  const reordenar = useCallback(async (cartao: KanbanCartao, novaPosicao: number): Promise<ResultadoComando> => {
    if (!cartao.osId) return { tipo: 'erro', message: MENSAGEM_FALHA_MOVER };
    return enviar(`/api/v1/kanban/cartoes/${cartao.osId}/reordenar`, 'POST', {
      novaPosicao,
      versao: cartao.versao,
    });
  }, []);

  const mover = useCallback(
    async (
      cartao: KanbanCartao,
      celulaDestino: string,
      novaPosicao: number,
      confirmarEmProducao?: boolean,
    ): Promise<ResultadoComando> => {
      if (!cartao.osId) return { tipo: 'erro', message: MENSAGEM_FALHA_MOVER };
      return enviar(`/api/v1/kanban/cartoes/${cartao.osId}/mover`, 'POST', {
        celulaDestino,
        novaPosicao,
        versao: cartao.versao,
        ...(confirmarEmProducao ? { confirmarEmProducao: true } : {}),
      });
    },
    [],
  );

  const alterarPrioridade = useCallback(async (opId: string, nivel: NivelPrioridadeKanban): Promise<ResultadoComando> => {
    try {
      const resposta = await apiMutate(`/api/v1/kanban/ops/${opId}/prioridade`, {
        method: 'PATCH',
        body: { nivel },
      });
      if (resposta.ok) return { tipo: 'ok', resultado: null };
      return await interpretar(resposta);
    } catch {
      return { tipo: 'erro', message: MENSAGEM_FALHA_MOVER };
    }
  }, []);

  const resolverConflito = useCallback(async (cartao: KanbanCartao): Promise<ResultadoComando> => {
    if (!cartao.osId) return { tipo: 'erro', message: MENSAGEM_FALHA_MOVER };
    return enviar(`/api/v1/kanban/cartoes/${cartao.osId}/conflito/resolver`, 'POST', undefined);
  }, []);

  const liberar = useCallback(
    async (opIds: string[], confirmacoes?: KanbanConfirmacaoLiberacao[]) => {
      try {
        const resposta = await apiMutate(`/api/v1/kanban/liberar`, {
          method: 'POST',
          body: { opIds, ...(confirmacoes ? { confirmacoes } : {}) },
        });
        if (resposta.ok) {
          const resultado = (await resposta.json()) as import('@pieltec/contracts').KanbanLiberarResultado;
          return { tipo: 'ok', resultado } as const;
        }
        // Preserva a mensagem do servidor (ex.: OP sem roteiro) em vez de descartá-la (WR-WEB-06).
        const falha = await interpretar(resposta, MENSAGEM_FALHA_LIBERAR);
        return { tipo: 'erro', message: falha.tipo === 'ok' ? MENSAGEM_FALHA_LIBERAR : falha.message } as const;
      } catch {
        return { tipo: 'erro', message: MENSAGEM_FALHA_LIBERAR } as const;
      }
    },
    [],
  );

  const reordenarFilaEntrada = useCallback(async (opId: string, novaPosicao: number): Promise<ResultadoComando> => {
    return enviar(`/api/v1/kanban/fila-entrada/${opId}/reordenar`, 'POST', {
      novaPosicao,
    });
  }, []);

  const bloquear = useCallback(async (osId: string, corpo: { tipoInterrupcaoId?: string; motivoOutro?: string; versao: number }): Promise<ResultadoComando> => {
    return enviar(`/api/v1/kanban/cartoes/${osId}/bloquear`, 'POST', corpo);
  }, []);

  const desbloquear = useCallback(async (osId: string, versao: number): Promise<ResultadoComando> => {
    return enviar(`/api/v1/kanban/cartoes/${osId}/desbloquear`, 'POST', { versao });
  }, []);

  return { reordenar, mover, alterarPrioridade, resolverConflito, liberar, reordenarFilaEntrada, bloquear, desbloquear };
}
